
import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, Trash2, Megaphone } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { Notification } from "@/hooks/useNotifications";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useChangelog } from "@/hooks/useChangelog";
import { ChangelogDialog } from "@/components/changelog/ChangelogDialog";

export function NotificationCenter() {
  const { t } = useTranslation();
  const { 
    useNotificationsQuery, 
    useUnreadCountQuery,
    useMarkAsReadMutation,
    useMarkAllAsReadMutation,
    useDeleteReadNotificationsMutation
  } = useNotifications();
  const { data: notifications = [], isLoading } = useNotificationsQuery();
  const { data: unreadCount = 0 } = useUnreadCountQuery();
  const markAsRead = useMarkAsReadMutation();
  const markAllAsRead = useMarkAllAsReadMutation();
  const deleteReadNotifications = useDeleteReadNotificationsMutation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [highlightChangelogId, setHighlightChangelogId] = useState<string | undefined>();

  // Changelog
  const { useUnreadChangelogsQuery } = useChangelog();
  const { data: unreadChangelogs = [] } = useUnreadChangelogsQuery();

  // Ensure unreadCount is treated as a number
  const unreadCountNumber = typeof unreadCount === 'number' ? unreadCount : 0;
  const totalUnread = unreadCountNumber + unreadChangelogs.length;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead.mutate(notification.id);
    
    if (notification.contractId) {
      navigate(`/contracts/${notification.contractId}`);
      setOpen(false);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleClearReadNotifications = () => {
    deleteReadNotifications.mutate();
  };

  const handleChangelogClick = (changelogId: string) => {
    setOpen(false);
    setHighlightChangelogId(changelogId);
    setChangelogOpen(true);
  };

  const getNotificationTypeStyle = (type: string) => {
    switch (type) {
      case "reminder": 
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "system": 
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "info":
      default: 
        return "bg-green-100 text-green-800 border-green-200";
    }
  };

  // Check if there are any read notifications
  const hasReadNotifications = notifications.some(notification => notification.isRead);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <Popover open={open} onOpenChange={handleOpenChange}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {totalUnread > 0 && (
                    <Badge 
                      className="absolute -top-2 -right-2 px-1.5 py-0.5 min-w-[1.2rem] h-5 flex items-center justify-center bg-red-500"
                      variant="destructive"
                    >
                      {totalUnread > 99 ? "99+" : totalUnread}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('header.notifications', 'Notifications')}</p>
            </TooltipContent>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="font-semibold">{t('header.notifications', 'Notifications')}</h3>
              <div className="flex gap-2">
                {unreadCountNumber > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleMarkAllAsRead} 
                    className="text-xs h-7"
                  >
                    {t('notifications.markAllRead', 'Mark all as read')}
                  </Button>
                )}
                {hasReadNotifications && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClearReadNotifications} 
                    className="text-xs h-7 text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    {t('notifications.clearRead', 'Clear read')}
                  </Button>
                )}
              </div>
            </div>
            <ScrollArea className="h-[400px]">
              {/* Unread Changelogs */}
              {unreadChangelogs.length > 0 && (
                <div>
                  {unreadChangelogs.map((changelog) => (
                    <div key={`changelog-${changelog.id}`}>
                      <Card
                        className="m-2 p-3 cursor-pointer border-l-4 border-l-primary bg-primary/5 hover:bg-primary/10 transition-colors"
                        onClick={() => handleChangelogClick(changelog.id)}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-1.5">
                            <Megaphone className="h-4 w-4 text-primary" />
                            <h4 className="font-medium text-sm">
                              v{changelog.version} — {changelog.title}
                            </h4>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(changelog.publishedAt, "MMM d")}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('changelog.newRelease', '{{count}} changes in this release', { count: changelog.changes.length })}
                        </p>
                      </Card>
                      <Separator />
                    </div>
                  ))}
                </div>
              )}

              {/* Regular Notifications */}
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  {t('notifications.loading', 'Loading notifications...')}
                </div>
              ) : notifications.length === 0 && unreadChangelogs.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  {t('notifications.empty', 'No notifications')}
                </div>
              ) : (
                <div>
                  {notifications.map((notification) => (
                    <div key={notification.id}>
                      <Card 
                        className={`m-2 p-3 cursor-pointer border-l-4 ${
                          notification.isRead ? "bg-muted/50" : "border-l-primary"
                        } ${getNotificationTypeStyle(notification.type)}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <h4 className={`font-medium ${notification.isRead ? "text-muted-foreground" : ""}`}>
                            {notification.title}
                          </h4>
                          <span className="text-xs text-muted-foreground">
                            {format(notification.createdAt, "MMM d, h:mm a")}
                          </span>
                        </div>
                        <p className="text-sm">{notification.message}</p>
                      </Card>
                      <Separator />
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            {/* Changelog link at bottom */}
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setOpen(false);
                  setHighlightChangelogId(undefined);
                  setChangelogOpen(true);
                }}
              >
                <Megaphone className="h-3 w-3 mr-1.5" />
                {t('changelog.viewAll', 'View all changelogs')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        </Tooltip>
      </TooltipProvider>

      <ChangelogDialog
        open={changelogOpen}
        onOpenChange={setChangelogOpen}
        highlightId={highlightChangelogId}
      />
    </>
  );
}
